export { createQueryClient, retryServerErrors } from "./queryClient";
export type { QueryLogger, CreateQueryClientOptions } from "./queryClient";

export {
  beginClientSignOut,
  isClientSignOutInProgress,
  isSignOutApiBlocked,
  resetClientSignOutGate,
  runSignOutTeardownOps,
} from "./signOutGate";

export { QueryProvider } from "./QueryProvider";

export type { ApiFetcher } from "./apiFetcher";
export { ApiProvider, useApiFetcher } from "./ApiProvider";

export { QUERY_KEYS } from "./queryKeys";

export {
  createQueuedInvalidator,
} from "./batchedInvalidate";
export type {
  QueuedInvalidationOptions,
  QueueInvalidate,
} from "./batchedInvalidate";

export {
  formatErrorForLog,
  isNetworkError,
  isAuthError,
  isNoActiveSessionError,
  isSignOutAbortedError,
  shouldSuppressClientSignOutError,
  trackNetworkError,
} from "./errorUtils";

export {
  ACTIVE_SESSION_NULL_RETRY_DELAYS_MS,
  fetchActiveSessionWithRetry,
  reconcileActiveSessionViaSync,
  isAbortError,
} from "./lib/fetchActiveSessionWithRetry";
export type {
  FetchActiveSessionWithRetryParams,
  ReconcileActiveSessionParams,
  SessionApiFetcher,
} from "./lib/fetchActiveSessionWithRetry";

export {
  notifyWakeSignals,
  registerWakeRecoveryJob,
  unregisterWakeRecoveryJob,
  setWakeRecoveryLogger,
  setWakeRecoveryDebounceMs,
  getWakeRecoveryMetricsSnapshot,
  resetWakeRecoveryMetricsForTests,
} from "./wakeRecoveryGate";
export type {
  WakeSignal,
  WakeRecoveryContext,
  WakeRecoveryJob,
  WakeRecoveryLogger,
  WakeRecoveryCycleRecord,
} from "./wakeRecoveryGate";

export type {
  ActiveTask,
  ActiveTaskResponse,
  CalendarSettings,
  UserSettings,
  Task,
  ExternalTaskSource,
  ServiceMetadata,
  ServiceMetadataWithConnection,
  GetTasksParams,
  GetTasksResponse,
  CreateTaskInput,
  ExternalTasksResponse,
  BonusEntry,
  BonusAvailableResponse,
  BonusHistoryResponse,
  BonusTomorrowResponse,
  AvailableActivityInfo,
  AvailableSessionPoints,
  StartupPointsResponse,
  SubscriptionStatus,
  Channel,
  UserChannelsResponse,
  ChatMessage,
  ChatMessagesResponse,
  CalendarEvent,
  Playlist,
  PlaylistStats,
  PlaylistAnalyticsResponse,
  AppVersionResponse,
  Personality,
} from "./types";
export {
  TODO_GROUP_SLUG,
  THIS_SESSION_GROUP_SLUG,
  COMPLETED_GROUP_SLUG,
} from "./types";

// Timer session hooks
export {
  useActiveSession,
  useStartSession,
  useEndSession,
  usePauseSession,
  useStartBreak,
  useResumeSession,
  useEndBreak,
  useSyncSession,
  useChangeInSession,
  useResetBreakInterval,
  useResetFocusInterval,
  normalizeActiveSessionResponse,
} from "./hooks/useTimerSession";
export type {
  ActiveSessionData,
  SessionData,
  BackendActiveSessionResponse,
  TimerHookConfig,
  TimerMutationCallbacks,
  UseActiveSessionOptions,
  PauseResumeVars,
  ChangeInSessionVars,
  ResetIntervalVars,
  EndSessionVars,
} from "./hooks/useTimerSession";

// Fresh elapsed-time reads without cache subscriptions
export { getLastKnownSessionElapsedSeconds } from "./activeSessionFreshness";

// Narrow activeSession cache slices (no fetching; see useActiveSessionSlice)
export {
  useActiveSessionSlice,
  useActiveSessionId,
  useActiveSessionIsPaused,
  useActiveSessionStartedAt,
  useActiveSessionLoaded,
  getActiveSessionSnapshot,
} from "./hooks/useActiveSessionSlice";

// Active task hook
export { useActiveTask } from "./hooks/useActiveTask";

// Distraction hooks
export { useDistractions } from "./hooks/useDistractions";
export type {
  DistractionRow,
  DistractionsByType,
  UseDistractionsOptions,
} from "./hooks/useDistractions";

// Connection status hook + provider
export {
  ConnectionStatusProvider,
  useConnectionStatus,
} from "./ConnectionStatusProvider";
export {
  reportDesktopExtensionAlive,
  getLastKnownPhoneStatus,
  mapRowStatusToConnection,
  resolveDesktopConnectionStatus,
  resolvePhoneConnectionStatus,
  useConnectionStatusValue,
} from "./hooks/useConnectionStatus";
export type {
  ConnectionStatus,
  ConnectionStatusData,
  DistractionRowStatus,
} from "./hooks/useConnectionStatus";

// User settings hook
export { useUserSettings } from "./hooks/useUserSettings";

// Calendar settings (from user_external_services only)
export { useCalendarSettings } from "./hooks/useCalendarSettings";
export { useCalendarConnection } from "./hooks/useCalendarConnection";
export { getCalendarConnection } from "./utils/calendarConnection";
export type { CalendarConnection } from "./utils/calendarConnection";
export type { UseCalendarConnectionResult } from "./hooks/useCalendarConnection";

// Productivity hooks
export {
  useProductivityChart,
  useProductivityLeaderboard,
  useRecentSessions,
  usePaginatedRecentSessions,
  useUserStats,
  useStreak,
  useActivityStreak,
  useSessionHistory,
} from "./hooks/useProductivity";
export type {
  StreakData,
  LeaderboardPeriod,
} from "./hooks/useProductivity";

// Task hooks
export { useTaskServices, useTaskService } from "./hooks/useTaskServices";
export { useExternalTasks } from "./hooks/useExternalTasks";
export { useThisSessionTasks } from "./hooks/useThisSessionTasks";
export {
  useSyncExternalTask,
  useSyncAllExternalTasks,
} from "./hooks/useSyncExternalTasks";
export { useCreateTodoTask, useDeleteTask } from "./hooks/useTasks";
export { useOrganizableTasks } from "./hooks/useOrganizableTasks";
export { useSimplifiableTasks } from "./hooks/useSimplifiableTasks";

// Bonus & Points hooks
export {
  useBonusAvailable,
  useBonusHistory,
  useBonusTomorrow,
} from "./hooks/useBonus";
export { useAvailableSessionPoints } from "./hooks/useAvailableSessionPoints";
export { useStartupPoints } from "./hooks/useStartupPoints";
export {
  useSessionMissedOpportunities,
  type MissedOpportunity,
  type SessionMissedOpportunitiesResponse,
} from "./hooks/useSessionMissedOpportunities";

// Subscription hooks
export { useSubscriptionStatus } from "./hooks/useSubscriptionStatus";
export { useWatchSubscription } from "./hooks/useWatchSubscription";

// Session feedback
export { useSessionFeedback } from "./hooks/useSessionFeedback";

// Chat hooks
export { useChannelDetails } from "./hooks/useChannelDetails";
export {
  useCoworkingUsers,
  type UseCoworkingUsersOptions,
} from "./hooks/useCoworkingUsers";

// Break actions
export { useGetRandomVideo, useBreakActivity } from "./hooks/useBreakActions";

// Calendar hooks
export {
  useCalendarEvents,
  useCreateCalendarEvent,
  useUpdateCalendarEvent,
  useDeleteCalendarEvent,
  // Deprecated aliases (backward compat)
  useGoogleCalendarEvents,
  useCreateGoogleCalendarEvent,
  useUpdateGoogleCalendarEvents,
  useDeleteGoogleCalendarEvent,
} from "./hooks/useCalendar";
export { usePublicCalendar } from "./hooks/usePublicCalendar";

// Playlist hooks
export {
  usePlaylistAnalytics,
  usePlaylist,
  usePlaylistBadges,
} from "./hooks/usePlaylistAnalytics";

// Other hooks
export { useAppVersion } from "./hooks/useAppVersion";
export { usePersonalities } from "./hooks/usePersonalities";
export { useFeedback } from "./hooks/useFeedback";
export { useCoffeeShopImage } from "./hooks/useCoffeeShopImage";
export { useUpdateUser } from "./hooks/useUpdateUser";
